import { type App, inject, ref, type Ref } from 'vue'
import type { IntercomPlugin } from '@/third-party/intercom.ts'
import type { AuthPlugin, IntercomUser } from '@/plugins/auth.ts'
import type { Router } from 'vue-router'
import type { ReactiveCookie } from '@/plugins/cookie-plugin.ts'
import { getDocument, getTimeout, getWindow } from '@/helpers/globals.ts'
// import type { RegionCode } from '@/graphql/api'

export interface IntercomLauncherPlugin {
    appId: string | undefined

    isLoaded: Ref<boolean>
    unreadCount: Ref<number>
    chatStarted: Ref<boolean>

    show(): Promise<void>
    loadIntercom(): Promise<void>
    unloadIntercom(): void

    showZipchat: Readonly<Ref<boolean>>
}

const key = 'app.plugin.intercom-launcher'

export function installIntercomLauncher(
    app: App,
    intercom: IntercomPlugin,
    auth: AuthPlugin,
    router: Router,
    cookie: ReactiveCookie<IntercomCookieVariables>
    // regionCode: Ref<Readonly<RegionCode>>,
    // isWorkingHoursUk: Readonly<Ref<boolean>>
): IntercomLauncherPlugin {
    const plugin = createIntercomLauncher(
        intercom,
        auth,
        router,
        cookie
        // regionCode,
        // isWorkingHoursUk
    )

    app.provide(key, plugin)

    return plugin
}

export function useIntercomLauncher(): IntercomLauncherPlugin {
    return inject(key) as IntercomLauncherPlugin
}

export function createIntercomLauncher(
    intercom: IntercomPlugin,
    auth: AuthPlugin,
    router: Router,
    cookie: ReactiveCookie<IntercomCookieVariables>
    // regionCode: Ref<Readonly<RegionCode>>,
    // isWorkingHoursUk: Readonly<Ref<boolean>>
): IntercomLauncherPlugin {
    const window = getWindow()
    const document = getDocument()
    const timeout = getTimeout()

    const { appId, chatStarted, unreadCount, isLoaded, load, unload } = intercom
    const getIntercomSettings = createIntercomSettingsGetter(
        appId,
        auth,
        router,
        cookie
    )

    const startChat = () => {
        chatStarted.value = true
    }

    const stopChat = () => {
        chatStarted.value = false
    }

    async function loadIntercom(): Promise<void> {
        if (!window) {
            return
        }

        await load()

        window.Intercom('onShow', startChat)

        window.Intercom('onHide', stopChat)

        window.Intercom('onUnreadCountChange', (count: number) => {
            unreadCount.value = count
        })

        const intercomSettings = getIntercomSettings()

        window.Intercom('boot', intercomSettings)
    }

    // const showZipchat = computed(
    //     () => regionCode.value === 'UK' && !isWorkingHoursUk.value
    // )
    const showZipchat = ref(false)

    async function show(): Promise<void> {
        if (showZipchat.value) {
            // show zipchat
            const getZipchat = () =>
                document
                    ?.getElementById('widget-chat-container')
                    ?.querySelector('input')
            let zipchat = getZipchat()
            zipchat?.click()

            if (!zipchat) {
                timeout?.setTimeout(() => {
                    zipchat = getZipchat()
                    zipchat?.click()
                }, 6_000)
            }
        } else {
            try {
                await loadIntercom()
                window?.Intercom('show')
            } catch (e) {
                alert(e)
            }
        }
    }

    // const router = useRouter()
    router.afterEach(() => {
        if (isLoaded.value) {
            window?.Intercom?.('update', {
                last_request_at: new Date().getTime() / 1000,
            })
        }
    })

    // onUnmounted(() => {
    //     unload()
    // })

    return {
        appId,
        isLoaded,
        unreadCount,
        chatStarted,
        show,
        loadIntercom,
        unloadIntercom: unload,
        showZipchat,
    }
}

export type IntercomBaseSettings = {
    app_id: string
    custom_launcher_selector: '.intercom-launcher'
    hide_default_launcher: true
}
export type IntercomCookieVariables = {
    taylorandhart_referrer?: string
    utm_source?: string
    utm_medium?: string
    utm_campaign?: string
    utm_content?: string
    utm_term?: string
}
export type IntercomSettings = IntercomBaseSettings &
    IntercomCookieVariables &
    IntercomUser

export function createIntercomSettingsGetter(
    APP_ID: string | undefined,
    auth: AuthPlugin,
    router: Router,
    cookie: ReactiveCookie<IntercomCookieVariables>
): () => IntercomCookieVariables {
    const document = getDocument()
    const { getIntercomUser } = auth
    const query = router.currentRoute.value.query

    const getIntercomCookieVariables = () => {
        const variables: IntercomCookieVariables = {}

        if (cookie.value) {
            Object.assign(variables, cookie.value)
        }

        if (document && !variables.taylorandhart_referrer) {
            variables.taylorandhart_referrer = document.referrer.substring(
                0,
                255
            )
        }

        if (query['utm_source']) {
            variables.utm_source = query['utm_source'] as string
        }

        if (query['utm_medium']) {
            variables.utm_medium = query['utm_medium'] as string
        }

        if (query['utm_campaign']) {
            variables.utm_campaign = query['utm_campaign'] as string
        }

        if (query['utm_content']) {
            variables.utm_content = query['utm_content'] as string
        }

        if (query['utm_term']) {
            variables.utm_term = query['utm_term'] as string
        }

        cookie.value = variables

        return variables
    }

    return (): IntercomSettings => {
        const intercomSettings = {
            app_id: APP_ID,
            custom_launcher_selector: '.intercom-launcher',
            hide_default_launcher: true,
        } as IntercomSettings

        const intercomUser = getIntercomUser()

        if (intercomUser) {
            Object.assign(intercomSettings, intercomUser)
        }

        const intercomCookieVariables = getIntercomCookieVariables()

        Object.assign(intercomSettings, intercomCookieVariables)

        return intercomSettings
    }

    // return {
    //     getIntercomSettings,
    // }
}
